import React, { useEffect, useMemo, useState } from "react";
import { Button, Loader, Message } from "semantic-ui-react";
import { toast } from "react-toastify";
import { useAuth } from "../../../../hooks";
import { BalanceHidricoForm } from "../../../../api";
import { leerPeriodoAgua } from "../adaptadorAgua";
import { recalcularBalance } from "../waterBalanceCalc";
import { toLocalState, toPayload, buildEstadoParaMotor } from "../estado";
import { TipologiaStep } from "../TipologiaStep";
import { ContextoStep } from "../ContextoStep";
import { EntradasHeredadasStep } from "../EntradasHeredadasStep";
import { CentrosStep } from "../CentrosStep";
import { InstrumentosStep } from "../InstrumentosStep";
import { FichasDeCampoStep } from "../FichasDeCampoStep";
import { SalidasYAlmacenamientoStep } from "../SalidasYAlmacenamientoStep";
import { CostosStep } from "../CostosStep";
import { CierreStep } from "../CierreStep";
import { InformeReport } from "../InformeReport";
import { useWaterBalanceT } from "../useWaterBalanceT";
import "./WaterBalanceWorkspace.scss";

const balanceCtrl = new BalanceHidricoForm();

// Orquestador del wizard, calcado de EnergyPerformanceWorkspace: mantiene un
// único estado local editable, delega cada panel a un step, y persiste todo
// junto con "Guardar avance" — igual que EnergyPerformanceInstallation, acá
// solo se guarda lo editable; lo derivado (semáforo, índice, informe) se
// recalcula siempre con waterBalanceCalc.js y nunca se persiste.
//
// `step` es controlado desde afuera (BalanceSidebar, ver navPorModo.js): la
// navegación vive en un solo panel combinado con el selector de modo, no
// adentro de cada workspace (Sustemia_BalanceHidrico_Modulo_ES_v1.html).
//
// `seedLocal`/`esDemo`: usados por el modo "Planta de ejemplo", que reutiliza
// este mismo wizard con datos ficticios ya cargados en vez de arrancar
// vacío, y sin persistir nada (ver ../datosDemo.js).
export function WaterBalanceWorkspace({ balance, siteSelected, period, year, waterDoc, productionDoc, socialDoc, onBalanceSaved, seedLocal, esDemo, step }) {
  const t = useWaterBalanceT();
  const { accessToken } = useAuth();
  const [local, setLocal] = useState(() => (seedLocal ? { ...toLocalState(null), ...seedLocal } : toLocalState(balance)));
  const [guardando, setGuardando] = useState(false);

  const balanceId = balance?._id;
  useEffect(() => {
    if (esDemo) return; // el demo no se resetea contra un balance real: no existe.
    setLocal(toLocalState(balance));
  }, [balanceId, esDemo]);

  const heredado = useMemo(
    () => leerPeriodoAgua(waterDoc, productionDoc, local.sourceAnnotations, socialDoc),
    [waterDoc, productionDoc, socialDoc, local.sourceAnnotations]
  );

  const resultado = useMemo(() => recalcularBalance(buildEstadoParaMotor(local, heredado)), [local, heredado]);

  const guardar = async () => {
    if (esDemo) {
      toast.info(t("water_balance_demo_no_save_toast"));
      return;
    }
    setGuardando(true);
    try {
      const payload = toPayload(local, { site: siteSelected, period, year });
      if (!balance?._id) {
        const res = await balanceCtrl.createBalanceHidrico(accessToken, payload);
        if (res.code === 200) {
          toast.success(t("water_balance_saved_toast"));
          onBalanceSaved(res.balance);
        }
      } else {
        const res = await balanceCtrl.updateBalanceHidrico(accessToken, balance._id, payload);
        if (res.code === 200) {
          toast.success(t("water_balance_saved_toast"));
          onBalanceSaved({ ...balance, ...payload });
        }
      }
    } catch (error) {
      toast.error(error?.msg || error?.message || t("water_balance_save_error_toast"));
    } finally {
      setGuardando(false);
    }
  };

  const subirEvidencia = async (file) => {
    try {
      const formData = new FormData();
      formData.append("files", file);
      const res = await balanceCtrl.uploadFile(accessToken, formData);
      if (res.code === 200) return res.files[0];
      return null;
    } catch (error) {
      toast.error(t("water_balance_upload_error_toast"));
      return null;
    }
  };

  return (
    <div className="water-balance-workspace">
      <div className="water-balance-workspace__topbar">
        <span className="water-balance-workspace__tag">{esDemo ? t("water_balance_modo_demo_titulo") : t("water_balance_page_tag")}</span>
        {!esDemo && <span className="water-balance-workspace__name">{period} · {year}</span>}
        <Button primary size="small" loading={guardando} onClick={guardar} style={{ marginLeft: "auto" }}>
          {t("water_balance_save_progress_button")}
        </Button>
      </div>

      <div className="water-balance-workspace__main">
        {step === "tipologia" && <TipologiaStep value={local} onChange={setLocal} />}

        {step === "contexto" && (
          <ContextoStep
            value={local.context}
            onChange={(context) => setLocal({ ...local, context })}
            inheritedProduction={heredado.context.production}
            inheritedOperatingDays={heredado.context.operatingDays}
            inheritedStaffing={heredado.context.staffing}
          />
        )}

        {step === "entradas" && (
          <EntradasHeredadasStep
            heredado={heredado}
            waterDoc={waterDoc}
            anotaciones={local.sourceAnnotations}
            onChangeAnotaciones={(sourceAnnotations) => setLocal({ ...local, sourceAnnotations })}
            divergencias={local.divergences}
            onChangeDivergencias={(divergences) => setLocal({ ...local, divergences })}
            onChangePropuestas={(updater) => setLocal((prev) => ({ ...prev, correctionProposals: updater(prev.correctionProposals) }))}
          />
        )}

        {step === "centros" && (
          <CentrosStep
            typology={local.typology}
            hasLaundry={local.hasLaundry}
            centers={local.centers}
            onChange={(centers) => setLocal({ ...local, centers })}
          />
        )}

        {step === "instrumentos" && (
          <InstrumentosStep instruments={local.instruments} onChange={(instruments) => setLocal({ ...local, instruments })} />
        )}

        {step === "fichas" && (
          <FichasDeCampoStep
            typology={local.typology}
            heredado={heredado}
            executedFieldCases={local.executedFieldCases}
            onChangeCasosEjecutados={(executedFieldCases) => setLocal({ ...local, executedFieldCases })}
            onUploadEvidencia={subirEvidencia}
          />
        )}

        {step === "salidas" && (
          <SalidasYAlmacenamientoStep
            centers={local.centers}
            outputs={local.outputs}
            onChangeSalidas={(outputs) => setLocal({ ...local, outputs })}
            storage={local.storage}
            onChangeAlmacenamiento={(storage) => setLocal({ ...local, storage })}
            instruments={local.instruments}
            onUploadEvidencia={subirEvidencia}
          />
        )}

        {step === "costos" && (
          <CostosStep
            costs={local.costs}
            onChange={(costs) => setLocal({ ...local, costs })}
            centers={local.centers}
            outputs={local.outputs}
            lossClassification={local.lossClassification}
          />
        )}

        {step === "cierre" && (
          <CierreStep
            resultado={resultado}
            lossClassification={local.lossClassification}
            onChangeClasificacion={(lossClassification) => setLocal({ ...local, lossClassification })}
            divergenciasSinResolver={resultado.bloqueos.divergenciasSinResolver ? (local.divergences || []).filter((d) => d.status === "sin_resolver").length : 0}
            closed={local.closed}
            onToggleCerrado={(closed) => setLocal({ ...local, closed })}
          />
        )}

        {step === "informe" && (
          <InformeReport
            typology={local.typology}
            period={period}
            year={year}
            resultado={resultado}
            production={heredado.context.production}
            executedFieldCases={local.executedFieldCases}
            costs={local.costs}
            centers={local.centers}
            outputs={local.outputs}
            instruments={local.instruments}
            inputs={heredado.inputs}
            lossClassification={local.lossClassification}
            correctionProposals={local.correctionProposals}
            context={{ ...local.context, operatingDays: heredado.context.operatingDays, staffing: heredado.context.staffing }}
            hasLaundry={local.hasLaundry}
          />
        )}
      </div>
    </div>
  );
}

export default WaterBalanceWorkspace;
